import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Activity,
  AlertTriangle,
  Briefcase,
  CalendarClock,
  CheckCircle2,
  FileSignature,
  Handshake,
} from "lucide-react";
import { AdminPage } from "@/components/admin/AdminSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  DashboardFilters,
  defaultFilters,
  ALL_AGENCIAS,
  type DashboardFiltersState,
} from "@/components/admin/dashboard/DashboardFilters";
import {
  EquipoTablas,
  TIPOS_ORDER,
  TIPO_LABEL,
  type AgentRow,
} from "@/components/admin/dashboard/EquipoTablas";
import {
  getDashboardContext,
  getMyCommercialSummary,
  getTeamCommercialSummary,
  listDashboardAgencias,
} from "@/lib/dashboard.functions";

export const Route = createFileRoute("/admin/")({
  component: DashboardPage,
});

const nf = new Intl.NumberFormat("es-ES");

function toPayload(f: DashboardFiltersState) {
  return {
    from: `${f.from}T00:00:00.000Z`,
    to: `${f.to}T23:59:59.999Z`,
    agencia_id: f.agenciaId === ALL_AGENCIAS ? null : f.agenciaId,
    granularity: f.granularity,
  };
}

function Kpi({
  label,
  value,
  icon: Icon,
  suffix,
  loading,
}: {
  label: string;
  value: number | null | undefined;
  icon: React.ElementType;
  suffix?: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <p className="mt-2 text-3xl font-bold tracking-tight text-foreground">
          {loading ? "…" : value === null || value === undefined ? "—" : `${nf.format(value)}${suffix ?? ""}`}
        </p>
      </CardContent>
    </Card>
  );
}

function bucketLabel(b: string, g: string) {
  if (g === "month") return format(new Date(`${b}-01T00:00:00`), "MMM yyyy", { locale: es });
  return format(new Date(`${b}T00:00:00`), "d MMM", { locale: es });
}

function ResumenAgente({
  agent,
  serie,
  granularity,
  loading,
  vencidas,
  sinActividad,
}: {
  agent: AgentRow | undefined;
  serie: { bucket: string; value: number }[];
  granularity: string;
  loading: boolean;
  vencidas: { id: string; titulo: string; tipo: string; fin_at: string | null }[];
  sinActividad: {
    id: string;
    referencia: string;
    titulo: string | null;
    estado: string;
    expediente?: string | null;
    dias_sin_actualizar?: number | null;
  }[];
}) {
  const tipoData = useMemo(
    () =>
      TIPOS_ORDER.map((t) => ({ tipo: TIPO_LABEL[t], value: agent?.porTipo?.[t] ?? 0 })).filter(
        (d) => d.value > 0,
      ),
    [agent],
  );

  const serieData = serie.map((s) => ({ ...s, label: bucketLabel(s.bucket, granularity) }));

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
          Operaciones y expedientes
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Operaciones activas" value={agent?.expedientes_activos ?? 0} icon={Briefcase} />
          <Kpi
            label="Negociaciones abiertas"
            value={agent?.negociaciones_abiertas ?? 0}
            icon={Handshake}
          />
          <Kpi
            label="Operaciones cerradas (mes)"
            value={agent?.operaciones_cerradas_mes ?? 0}
            icon={CheckCircle2}
          />
          <Kpi
            label="Expedientes completos"
            value={agent?.expedientes_completos_pct ?? null}
            suffix=" %"
            icon={CheckCircle2}
          />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
          Actividad y seguimiento
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Actividades registradas" value={agent?.actividades ?? 0} icon={Activity} />
          <Kpi
            label="Próximas acciones vencidas"
            value={agent?.acciones_vencidas ?? 0}
            icon={CalendarClock}
          />
          <Kpi
            label="Operaciones sin actualizar (+14 días)"
            value={agent?.operaciones_sin_actualizar ?? 0}
            icon={AlertTriangle}
          />
          <Kpi
            label="Expedientes sin actividad"
            value={agent?.expedientes_sin_actividad ?? 0}
            icon={AlertTriangle}
          />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Otras métricas</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Kpi
            label="Firmas conseguidas"
            value={agent?.firmas_total ?? 0}
            icon={FileSignature}
          />
          <Kpi label="Firmas del periodo" value={agent?.firmas ?? 0} icon={FileSignature} />
        </div>
      </div>


      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Actividad por tipo</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {tipoData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tipoData} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" allowDecimals={false} fontSize={12} />
                  <YAxis type="category" dataKey="tipo" width={110} fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground">Sin actividad en el periodo.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Evolución de actividades</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {serieData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={serieData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="label" fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="Actividades"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground">Sin actividad en el periodo.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Acciones vencidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {vencidas.length ? (
              vencidas.map((v) => (
                <Link
                  key={v.id}
                  to="/admin/tareas"
                  className="flex items-center justify-between rounded-md border p-2 text-sm hover:bg-accent"
                >
                  <span className="truncate">{v.titulo}</span>
                  <span className="ml-2 flex shrink-0 items-center gap-2">
                    <Badge variant="outline">{TIPO_LABEL[v.tipo] ?? v.tipo}</Badge>
                    {v.fin_at && (
                      <span className="text-muted-foreground">
                        {format(new Date(v.fin_at), "d MMM", { locale: es })}
                      </span>
                    )}
                  </span>
                </Link>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Nada vencido. Buen trabajo.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Operaciones sin actualizar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {sinActividad.length ? (
              sinActividad.map((t) => (
                <Link
                  key={t.id}
                  to="/admin/operaciones"
                  className="flex items-center justify-between rounded-md border p-2 text-sm hover:bg-accent"
                >
                  <span className="truncate">
                    {t.titulo || t.referencia}
                    {t.dias_sin_actualizar != null && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t.dias_sin_actualizar} días sin actualizar
                      </span>
                    )}
                  </span>
                  <span className="ml-2 flex shrink-0 items-center gap-1">
                    {t.expediente && (
                      <Badge
                        variant="outline"
                        className={
                          t.expediente === "verde"
                            ? "border-emerald-500/40 text-emerald-600"
                            : t.expediente === "amarillo"
                              ? "border-amber-500/40 text-amber-600"
                              : "border-destructive/40 text-destructive"
                        }
                      >
                        Expediente
                      </Badge>
                    )}
                    <Badge variant="outline">{t.estado}</Badge>
                  </span>
                </Link>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Todas actualizadas recientemente.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DashboardPage() {
  const [filters, setFilters] = useState<DashboardFiltersState>(defaultFilters);
  const [tab, setTab] = useState("mio");

  const listAgencias = useServerFn(listDashboardAgencias);
  const getContext = useServerFn(getDashboardContext);
  const getMine = useServerFn(getMyCommercialSummary);
  const getTeam = useServerFn(getTeamCommercialSummary);

  const ctxQ = useQuery({ queryKey: ["dashboard-ctx"], queryFn: () => getContext() });
  const agenciasQ = useQuery({
    queryKey: ["dashboard-agencias"],
    queryFn: () => listAgencias(),
  });

  const payload = useMemo(() => toPayload(filters), [filters]);

  const mineQ = useQuery({
    queryKey: ["dashboard-mine", payload],
    queryFn: () => getMine({ data: payload }),
  });

  const canSeeTeam = ctxQ.data?.canSeeTeam ?? false;
  const teamQ = useQuery({
    queryKey: ["dashboard-team", payload],
    queryFn: () => getTeam({ data: payload }),
    enabled: canSeeTeam && tab === "equipo",
  });

  const teamAgents = (teamQ.data?.agents ?? []) as AgentRow[];

  return (
    <AdminPage title="Dashboard" description="Resumen comercial de actividad y operaciones.">
      <div className="space-y-4">
        <DashboardFilters
          value={filters}
          onChange={setFilters}
          agencias={agenciasQ.data ?? []}
        />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="mio">Mi resumen</TabsTrigger>
            {canSeeTeam && <TabsTrigger value="equipo">Equipo</TabsTrigger>}
          </TabsList>

          <TabsContent value="mio" className="mt-4">
            <ResumenAgente
              agent={(mineQ.data?.agents?.[0] as AgentRow | undefined) ?? undefined}
              serie={mineQ.data?.serie ?? []}
              granularity={filters.granularity}
              loading={mineQ.isLoading}
              vencidas={mineQ.data?.vencidas ?? []}
              sinActividad={mineQ.data?.sinActividad ?? []}
            />
          </TabsContent>

          {canSeeTeam && (
            <TabsContent value="equipo" className="mt-4 space-y-4">
              {teamQ.isLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Actividades por agente</CardTitle>
                    </CardHeader>
                    <CardContent className="h-72">
                      {teamAgents.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={teamAgents.map((a) => ({
                              nombre: a.nombre,
                              value: a.actividades,
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="nombre" fontSize={12} />
                            <YAxis allowDecimals={false} fontSize={12} />
                            <Tooltip />
                            <Bar
                              dataKey="value"
                              name="Actividades"
                              fill="hsl(var(--primary))"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Sin actividad del equipo en el periodo.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  <EquipoTablas agents={teamAgents} />
                </>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </AdminPage>
  );
}
